import React, {useState} from 'react';
import {View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {noop} from 'lodash';

import {Banner} from '/component/base/Banner/Banner';
import Button from '/component/base/Button';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {generateOpenDeletePopover} from '/component/screen/ReferralProfile/ReferralOfferExpiredBanner/ReferralOfferExpiredBanner.util';
import {DownlineProfile} from '/constant/TestIDs';
import sharedTestIDs from '/constant/TestIDs/Shared';
import {REPORT_ALL_REFERRALS} from '/enum/records.enum';
import {ACTION_TYPES, EVENT_NAMES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {
  getReferralState,
  useCoachAnalyticsData,
} from '/util/referralAnalytics.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import {styles} from './ReferralOfferExpiredBanner.style';

interface ReferralOfferExpiredBannerProps {
  // eslint-disable-next-line react/require-default-props
  popoverShow?: () => void;
  candidateId: string;
  deleteCandidate: (args?: {candidateId: string}) => Promise<void> | void;
  userFullName?: string;
  isExpired: boolean;
}

export const ReferralOfferExpiredBanner = ({
  popoverShow = noop,
  candidateId = '',
  deleteCandidate = noop,
  userFullName = '',
  isExpired = false,
}: ReferralOfferExpiredBannerProps) => {
  const {ts} = useTranslation('ReferralOfferExpiredBanner');
  const [expanded, setExpanded] = useState(true);
  const navigation = useNavigation();
  const coachAnalyticsData = useCoachAnalyticsData();

  const toggleExpand = () => {
    setExpanded(currentExpanded => !currentExpanded);
  };

  const handleDeleteProfile = async () => {
    // Track delete_click event
    const sendDeleteClickEvent =
      getTealiumUtilDefaultInstance().generateTrackEvent({
        eventName: EVENT_NAMES.profileUpdate,
      });

    const eventData = {
      action: ACTION_TYPES.remove,
      state: getReferralState(isExpired),
      user_full_name: userFullName,
      screen_name: REPORT_ALL_REFERRALS,
      coach_highest_rank: coachAnalyticsData.coachHighestRank,
      coach_id: coachAnalyticsData.coachId,
      ...coachAnalyticsData,
    };

    sendDeleteClickEvent(eventData);

    await deleteCandidate({candidateId});
    navigation.goBack();
  };
  return (
    <View style={styles.container}>
      <Banner style={{base: styles.base}} verticalBanner alertLevel="info">
        <TouchableOpacity
          accessibilityRole="button"
          style={styles.accordionHeader}
          onPress={toggleExpand}>
          <Icon name="alert" style={styles.alertButtonIcon} />
          <Text isBold style={styles.bannerText}>
            {ts('Accordion Title', 'Referral Offer has expired.')}
          </Text>
          <Icon
            name={expanded ? 'chevron-up' : 'chevron-down'}
            style={styles.accordionIcon}
          />
        </TouchableOpacity>
        {expanded && (
          <>
            <Text style={styles.accordionBody}>
              {'This person referral offer has expired.'}
            </Text>
            <Button
              type="textLink"
              onPress={generateOpenDeletePopover(
                popoverShow,
                handleDeleteProfile,
              )}
              style={{base: styles.btnStyle}}
              {...testProperties(DownlineProfile.mergeBannerArchive)}>
              <View
                style={styles.textStyle}
                {...testProperties(sharedTestIDs.view)}>
                <Icon
                  name={'trash'}
                  style={styles.trashIcon}
                  {...testProperties(DownlineProfile.mergeBannerArchiveIcon)}
                />

                <Text style={styles.removeLabel} isBold>
                  {ts('delete button', 'Remove candidate profile')}
                </Text>
              </View>
            </Button>
          </>
        )}
      </Banner>
    </View>
  );
};

ReferralOfferExpiredBanner.defaultProps = {
  userFullName: '',
};

export default ReferralOfferExpiredBanner;
